import { LinkExternal01 } from "@untitledui/icons";
import { useMemo, useState } from "react";
import { Button } from "@/components/base/buttons/button";
import { TabHeader } from "../tab-header";
import type { MerchantWithUsers } from "../types";
import { AdminRiskRow } from "./AdminRiskRow";
import { CustomRiskAdder } from "./CustomRiskAdder";
import { DomainKnowledge } from "./DomainKnowledge";
import { DeleteCustomRiskDialog, UnsavedChangesDialog } from "./dialogs";
import { MerchantInfo } from "./MerchantInfo";
import { RiskGroupCard } from "./RiskGroupCard";
import { groupAdminCatalog } from "./risk-grouping";
import { useSafetyConfigState } from "./useSafetyConfigState";

type SafetyTabProps = {
  merchant: MerchantWithUsers;
};

const SafetyTabContent = ({ merchant }: SafetyTabProps) => {
  const config = useSafetyConfigState(merchant);
  const [openGroups, setOpenGroups] = useState<Set<string>>(new Set());
  // Risk ids whose "Configure risk" accordion is open. Everything starts closed;
  // a freshly staged custom risk opens (and any previously staged ones close).
  const [openConfigs, setOpenConfigs] = useState<Set<string>>(new Set());

  const groups = useMemo(() => groupAdminCatalog(config.mergedCatalog), [config.mergedCatalog]);

  const toggleGroup = (id: string) => {
    setOpenGroups((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const openGroup = (id: string) => {
    setOpenGroups((prev) => (prev.has(id) ? prev : new Set(prev).add(id)));
  };

  const toggleConfig = (riskId: string) => {
    setOpenConfigs((prev) => {
      const next = new Set(prev);
      if (next.has(riskId)) next.delete(riskId);
      else next.add(riskId);
      return next;
    });
  };

  // Adding a custom risk lands it in the (collapsed-by-default) custom group —
  // open the group so the new row is visible.
  const handleCustomRiskSelect = (key: string | number | null) => {
    config.handleCustomRiskSelect(key);
    if (key != null) openGroup("custom");
  };

  const handleCreateNewCustomRisk = (name: string) => {
    const localKey = config.addNewCustomRisk(name);
    openGroup("custom");
    setOpenConfigs((prev) => {
      const next = new Set([...prev].filter((id) => !id.startsWith("__new_")));
      next.add(localKey);
      return next;
    });
  };

  if (config.isLoading || !config.initial) {
    return (
      <div className="flex flex-col gap-6">
        <TabHeader
          title="Safety Configuration"
          subtitle="Loading configuration..."
          hasChanges={false}
          isPending={false}
          onSave={() => {}}
          onCancel={() => {}}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <TabHeader
        title="Safety Configuration"
        subtitle="Configure the risks, severity, and per-risk detection for this merchant"
        sticky
        hasChanges={config.hasChanges}
        isPending={config.isPending}
        onSave={config.handleSave}
        onCancel={config.handleCancel}
        rightContent={
          <>
            <div className="flex items-center gap-2 px-3 py-1.5 bg-secondary rounded-full">
              <div className="w-2 h-2 bg-success-solid rounded-full" />
              <span className="text-sm font-medium text-success-primary">{config.enabledCount} risks active</span>
            </div>
            <Button
              size="sm"
              color="secondary"
              iconTrailing={LinkExternal01}
              href={`/merchant/${merchant.id}/safety-beta/configure`}
              target="_blank"
              rel="noopener noreferrer"
            >
              Open customer view
            </Button>
          </>
        }
      />

      {config.settings && <MerchantInfo settings={config.settings} />}

      <div className="flex flex-col gap-3">
        {groups.map((group) => (
          <RiskGroupCard
            key={group.id}
            group={group}
            activeCount={group.risks.filter((r) => config.risksState[r.id]?.enabled).length}
            isOpen={openGroups.has(group.id)}
            onToggle={() => toggleGroup(group.id)}
          >
            {group.risks.map((risk) => {
              const state = config.risksState[risk.id];
              if (!state) return null;
              return (
                <AdminRiskRow
                  key={risk.id}
                  risk={risk}
                  state={state}
                  hasChanges={config.hasChanges}
                  isConfigOpen={openConfigs.has(risk.id)}
                  onToggleConfig={() => toggleConfig(risk.id)}
                  effectiveRisk={config.effectiveRisk}
                  onToggle={config.toggleRisk}
                  onUpdate={config.updateRisk}
                  onSetName={config.setCustomRiskName}
                  onSetDescription={config.setCustomRiskReadableDescription}
                  onSetVision={config.setNewCustomRiskVision}
                  onRemoveNew={config.removeNewCustomRisk}
                  onDelete={config.setRiskToDelete}
                />
              );
            })}
            {group.id === "custom" && (
              <CustomRiskAdder
                search={config.customSearch}
                onSearchChange={config.setCustomSearch}
                options={config.customRiskOptions}
                onSelect={handleCustomRiskSelect}
                onCreateNew={handleCreateNewCustomRisk}
              />
            )}
          </RiskGroupCard>
        ))}
      </div>

      <DomainKnowledge
        rows={config.domainKnowledgeRows}
        onChange={config.setDomainKnowledgeRows}
        productInfo={config.productInfo}
        onProductInfoChange={config.setProductInfo}
      />

      <UnsavedChangesDialog blocker={config.blocker} />
      <DeleteCustomRiskDialog
        risk={config.riskToDelete}
        isPending={config.isDeletePending}
        onCancel={() => config.setRiskToDelete(null)}
        onConfirm={config.handleConfirmDelete}
      />
    </div>
  );
};

export const SafetyTab = ({ merchant }: SafetyTabProps) => <SafetyTabContent key={merchant.id} merchant={merchant} />;
